import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { removeToken, getUser, setUser } from '../lib/auth';
import { isNativeApp } from '../lib/nativePush';
import { isIOS } from '../lib/socialAuth';
import { showManageSubscriptions } from '../lib/revenuecat';
import { changePassword, getSubscription, getNotificationSettings, updateNotificationSettings, updateUserProfile, uploadProfilePhoto, deleteProfilePhoto, getMe, getOwnerWhoami, deleteAccount } from '../lib/api';
import { toast } from 'sonner';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import MobileNav from '../components/MobileNav';
import SupportChat from '../components/SupportChat';
import ImageCropModal from '../components/ImageCropModal';
import { LogOut, Bell, Lock, User, Pencil, X, Mail, Home, CreditCard, AlertTriangle, Crown, Users, ExternalLink, BookOpen, MessageSquare, Smartphone, Shield, Play, CheckCircle2, Camera, Trash2, Loader2, ChevronDown, Activity, ChevronRight, CalendarClock } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

// Sleek animated pill switch with glow when ON
const ToggleSwitch = ({ checked, onChange, disabled, activeColor = 'emerald', testId }) => {
  const gradient = {
    emerald: 'linear-gradient(135deg, #34D399 0%, #059669 100%)',
    blue: 'linear-gradient(135deg, #60A5FA 0%, #2563EB 100%)',
    purple: 'linear-gradient(135deg, #C084FC 0%, #7C3AED 100%)'
  }[activeColor];
  const glowColor = {
    emerald: 'rgba(16,185,129,0.45)',
    blue: 'rgba(59,130,246,0.45)',
    purple: 'rgba(168,85,247,0.45)'
  }[activeColor];
  return (
    <motion.button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={onChange}
      disabled={disabled}
      data-testid={testId}
      whileTap={{ scale: 0.94 }}
      className={`relative inline-flex h-8 w-[58px] flex-shrink-0 items-center rounded-full p-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-slate-300 ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
      animate={{
        background: checked ? gradient : '#E2E8F0',
        boxShadow: checked ? `0 4px 14px ${glowColor}` : '0 1px 2px rgba(0,0,0,0.06) inset'
      }}
      transition={{ duration: 0.35, ease: 'easeInOut' }}
      style={{ background: checked ? gradient : '#E2E8F0' }}
    >
      <motion.span
        className="relative z-10 h-6 w-6 rounded-full bg-white flex items-center justify-center"
        animate={{ x: checked ? 26 : 0 }}
        transition={{ type: 'spring', stiffness: 550, damping: 32 }}
        style={{ boxShadow: '0 2px 6px rgba(0,0,0,0.22)' }}
      >
        <motion.span
          className="rounded-full"
          animate={{
            width: checked ? 9 : 7,
            height: checked ? 9 : 7,
            backgroundColor: checked ? 'rgba(16,185,129,0.9)' : '#CBD5E1'
          }}
          transition={{ duration: 0.25 }}
        />
      </motion.span>
    </motion.button>
  );
};

export default function SettingsPage() {
  const [isNative, setIsNative] = useState(false);
  const [emailNotificationsEnabled, setEmailNotificationsEnabled] = useState(false);
  const [pushNotifications, setPushNotifications] = useState(true);
  const [notifOpen, setNotifOpen] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [emailRecipients, setEmailRecipients] = useState('both');
  const [notifyOnCleaningStart, setNotifyOnCleaningStart] = useState(false);
  const [notifyOnReady, setNotifyOnReady] = useState(false);
  const [notifyOnLateCheckout, setNotifyOnLateCheckout] = useState(false);
  const [loadingEmailSettings, setLoadingEmailSettings] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editName, setEditName] = useState('');
  const [editCompanyName, setEditCompanyName] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [subscription, setSubscription] = useState(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [user, setUserState] = useState(getUser());
  const [chatOpen, setChatOpen] = useState(false);
  const [photoUrl, setPhotoUrl] = useState(getUser()?.photoUrl || null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [cropSrc, setCropSrc] = useState(null);
  const [showCrop, setShowCrop] = useState(false);
  const [photoError, setPhotoError] = useState(false);
  const photoInputRef = useRef(null);
  const isAdmin = user?.role === 'admin';
  const [isOwner, setIsOwner] = useState(false);

  useEffect(() => {
    getOwnerWhoami().then((res) => setIsOwner(!!res.data?.is_owner)).catch(() => {});
  }, []);

  useEffect(() => {
    // Check for subscription success/cancel from Stripe redirect
    const subscriptionStatus = searchParams.get('subscription');
    if (subscriptionStatus === 'success') {
      toast.success('Subscription updated successfully!');
      // Remove the query param
      window.history.replaceState({}, '', '/settings');
    } else if (subscriptionStatus === 'canceled') {
      toast.info('Subscription change was canceled');
      window.history.replaceState({}, '', '/settings');
    }
  }, [searchParams]);

  useEffect(() => {
    // Check if running as native app
    setIsNative(isNativeApp());
    if (isAdmin) loadSubscription();  // billing is admin-only; avoid 403 for staff
    loadEmailNotificationSettings();
    // Hydrate profile (photoUrl) from DB so the avatar always reflects the source of truth
    getMe().then((res) => {
      const fresh = res.data || {};
      setPhotoUrl(fresh.photoUrl || null);
      setPhotoError(false);
      const merged = { ...getUser(), ...fresh };
      setUser(merged);
      setUserState(merged);
    }).catch(() => {});
  }, []);

  const loadSubscription = async () => {
    try {
      const response = await getSubscription();
      setSubscription(response.data);
    } catch (error) {
      console.error('Failed to load subscription:', error);
    }
  };

  const loadEmailNotificationSettings = async () => {
    try {
      const response = await getNotificationSettings();
      const email = response.data.emailCheckoutNotifications;
      const push = response.data.pushNotifications !== false;
      const recipients = response.data.emailRecipients || 'both';
      const cleaningStart = response.data.notifyOnCleaningStart || false;
      const ready = response.data.notifyOnReady || false;
      const lateCheckout = response.data.notifyOnLateCheckout || false;
      setPushNotifications(push);
      setEmailRecipients(recipients);
      setNotifyOnCleaningStart(cleaningStart);
      setNotifyOnReady(ready);
      setNotifyOnLateCheckout(lateCheckout);
      // Staff must always keep at least one alert channel on
      if (!isAdmin && !email && !push) {
        setEmailNotificationsEnabled(true);
        try {
          await updateNotificationSettings({
            emailCheckoutNotifications: true,
            pushNotifications: push,
            emailRecipients: recipients,
            notifyOnCleaningStart: cleaningStart,
            notifyOnReady: ready,
            notifyOnLateCheckout: lateCheckout
          });
        } catch (e) { /* non-blocking */ }
      } else {
        setEmailNotificationsEnabled(email);
      }
    } catch (error) {
      console.error('Failed to load email notification settings:', error);
    }
  };

  const persist = async (overrides) => {
    const payload = {
      emailCheckoutNotifications: emailNotificationsEnabled,
      pushNotifications: pushNotifications,
      emailRecipients: emailRecipients,
      notifyOnCleaningStart: notifyOnCleaningStart,
      notifyOnReady: notifyOnReady,
      notifyOnLateCheckout: notifyOnLateCheckout,
      ...overrides
    };
    await updateNotificationSettings(payload);
  };

  const handleTogglePushNotifications = async () => {
    const newValue = !pushNotifications;
    if (!isAdmin && !newValue && !emailNotificationsEnabled) {
      toast.error('Keep at least one alert on: email or push');
      return;
    }
    setPushNotifications(newValue);
    try {
      await persist({ pushNotifications: newValue });
      toast.success(newValue ? 'Push notifications on' : 'Push notifications off');
    } catch (error) {
      setPushNotifications(!newValue);
      toast.error('Failed to update push notifications');
    }
  };

  const handleToggleEmailNotifications = async () => {
    const newValue = !emailNotificationsEnabled;
    if (!isAdmin && !newValue && !pushNotifications) {
      toast.error('Keep at least one alert on: email or push');
      return;
    }
    setLoadingEmailSettings(true);
    setEmailNotificationsEnabled(newValue);
    try {
      await persist({ emailCheckoutNotifications: newValue });
      toast.success(newValue ? 'Email notifications on' : 'Email notifications off');
    } catch (error) {
      setEmailNotificationsEnabled(!newValue);
      toast.error('Failed to update email notifications');
    } finally {
      setLoadingEmailSettings(false);
    }
  };

  const handleToggleCleaningStartNotify = async () => {
    const newValue = !notifyOnCleaningStart;
    setNotifyOnCleaningStart(newValue);
    try {
      await persist({ notifyOnCleaningStart: newValue });
      toast.success(newValue ? 'You\'ll be alerted when cleaning starts' : 'Cleaning-start alerts off');
    } catch (error) {
      setNotifyOnCleaningStart(!newValue);
      toast.error('Failed to update notification settings');
    }
  };

  const handleToggleReadyNotify = async () => {
    const newValue = !notifyOnReady;
    setNotifyOnReady(newValue);
    try {
      await persist({ notifyOnReady: newValue });
      toast.success(newValue ? 'You\'ll be alerted when a property is ready' : 'Ready alerts off');
    } catch (error) {
      setNotifyOnReady(!newValue);
      toast.error('Failed to update notification settings');
    }
  };

  const handleToggleLateCheckoutNotify = async () => {
    const newValue = !notifyOnLateCheckout;
    setNotifyOnLateCheckout(newValue);
    try {
      await persist({ notifyOnLateCheckout: newValue });
      toast.success(newValue ? 'You\'ll be alerted about late checkouts' : 'Late checkout alerts off');
    } catch (error) {
      setNotifyOnLateCheckout(!newValue);
      toast.error('Failed to update notification settings');
    }
  };

  const handleChangeRecipients = async (value) => {
    const prev = emailRecipients;
    setEmailRecipients(value);
    if (emailNotificationsEnabled) {
      try {
        await persist({ emailCheckoutNotifications: true, emailRecipients: value });
        toast.success('Recipients updated');
      } catch (error) {
        setEmailRecipients(prev);
        toast.error('Failed to update recipients');
      }
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (!currentPassword || !newPassword) {
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }

    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setChangingPassword(true);
    try {
      await changePassword(currentPassword, newPassword);
      toast.success('Password changed successfully');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  const handleSaveProfile = async () => {
    const trimmed = (editName || '').trim();
    const trimmedCompany = (editCompanyName || '').trim();
    if (user && trimmed) {
      if (isAdmin && !trimmedCompany) {
        toast.error('Company name cannot be empty');
        return;
      }
      try {
        await updateUserProfile({ name: trimmed, ...(isAdmin ? { companyName: trimmedCompany } : {}) });
        const updatedUser = { ...user, name: trimmed, ...(isAdmin ? { companyName: trimmedCompany } : {}) };
        setUser(updatedUser);
        setUserState(updatedUser);
        toast.success('Profile updated');
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to update profile');
        return;
      }
    }
    
    if (currentPassword && newPassword) {
      await handleChangePassword({ preventDefault: () => {} });
    }
    
    setShowEditModal(false);
  };

  const handlePhotoSelect = (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    if (!f.type.startsWith('image/')) {
      toast.error('Please choose an image file');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setCropSrc(reader.result);
      setShowCrop(true);
    };
    reader.readAsDataURL(f);
  };

  const uploadCroppedPhoto = async (file) => {
    setUploadingPhoto(true);
    try {
      const res = await uploadProfilePhoto(file);
      const url = res.data.photoUrl;
      setPhotoUrl(url);
      setPhotoError(false);
      const updated = { ...user, photoUrl: url };
      setUser(updated);
      setUserState(updated);
      toast.success('Photo updated');
      setShowCrop(false);
      setCropSrc(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upload photo');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleRemovePhoto = async () => {
    setUploadingPhoto(true);
    try {
      await deleteProfilePhoto();
      setPhotoUrl(null);
      const updated = { ...user, photoUrl: null };
      setUser(updated);
      setUserState(updated);
      toast.success('Photo removed');
    } catch (err) {
      toast.error('Failed to remove photo');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleLogout = () => {
    removeToken();
    navigate('/login');
  };

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deletingAccount, setDeletingAccount] = useState(false);
  const isAdminUser = user?.role === 'admin';

  const handleDeleteAccount = async () => {
    if (deleteConfirmText.trim().toUpperCase() !== 'DELETE') return;
    setDeletingAccount(true);
    try {
      await deleteAccount();
      toast.success('Your account has been deleted.');
      removeToken();
      navigate('/login');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete account');
      setDeletingAccount(false);
    }
  };

  const openEditModal = () => {
    // Admin edits their own name AND the company name (company name shows on Home)
    setEditName(user?.name || '');
    setEditCompanyName(user?.companyName || '');
    setEditEmail(user?.email || '');
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setShowEditModal(true);
  };

  const handleManageBilling = () => {
    navigate('/billing');
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const getStatusBadge = (status) => {
    const styles = {
      trialing: 'bg-blue-100 text-blue-700',
      active: 'bg-emerald-100 text-emerald-700',
      past_due: 'bg-red-100 text-red-700',
      canceled: 'bg-slate-100 text-slate-700',
      canceling: 'bg-amber-100 text-amber-700',
      incomplete: 'bg-amber-100 text-amber-700'
    };
    
    const labels = {
      trialing: 'Trial',
      active: 'Active',
      past_due: 'Past Due',
      canceled: 'Canceled',
      incomplete: 'Incomplete'
    };
    
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-bold ${styles[status] || styles.incomplete}`}>
        {labels[status] || status}
      </span>
    );
  };

  return (
    <div className="min-h-screen flex flex-col" style={{
      background: 'linear-gradient(135deg, #EFF6FF 0%, #ECFDF5 100%)'
    }}>
      <MobileNav />

      <main className="flex-1 mobile-container md:!max-w-2xl pb-8">
        <motion.div 
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="text-4xl md:text-5xl font-bold gradient-text mb-3 pb-1">Settings</h1>
          <p className="text-slate-600 font-medium">Manage your account and subscription</p>
        </motion.div>

        <div className="space-y-6">
          {/* Account Info */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="card-3d p-6"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-emerald-500 flex items-center justify-center">
                  <User size={20} className="text-white" />
                </div>
                <h3 className="text-xl font-bold text-slate-900">Account</h3>
              </div>
              <button
                onClick={openEditModal}
                className="btn-3d btn-3d-outline h-10 px-4 rounded-xl font-semibold text-sm flex items-center gap-2"
                data-testid="edit-account-button"
              >
                <Pencil size={16} />
                Edit
              </button>
            </div>
            
            {/* Profile Photo */}
            <div className="flex flex-col items-center mb-4" data-testid="profile-photo-section">
              <div className="relative">
                <div className="w-24 h-24 rounded-full overflow-hidden ring-4 ring-white shadow-lg bg-gradient-to-br from-blue-500 to-emerald-500 flex items-center justify-center">
                  {photoUrl && !photoError ? (
                    <img src={photoUrl} alt="Profile" className="w-full h-full object-cover" onError={() => setPhotoError(true)} data-testid="profile-photo-img" />
                  ) : (
                    <span className="text-4xl font-bold text-white">{(user?.name || user?.email || '?').trim().charAt(0).toUpperCase()}</span>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => photoInputRef.current?.click()}
                  disabled={uploadingPhoto}
                  className="absolute -bottom-1 -right-1 w-9 h-9 rounded-full bg-white shadow-md border border-slate-200 flex items-center justify-center text-slate-700 hover:bg-slate-50 transition-colors disabled:opacity-60"
                  data-testid="upload-photo-button"
                  aria-label="Change photo"
                >
                  {uploadingPhoto ? <Loader2 size={16} className="animate-spin" /> : <Camera size={16} />}
                </button>
              </div>
              {photoUrl && (
                <button
                  type="button"
                  onClick={handleRemovePhoto}
                  disabled={uploadingPhoto}
                  className="mt-4 inline-flex items-center gap-1.5 text-xs font-semibold text-red-500 hover:text-red-600 transition-colors disabled:opacity-60"
                  data-testid="remove-photo-button"
                >
                  <Trash2 size={13} /> Remove photo
                </button>
              )}
              <input ref={photoInputRef} type="file" accept="image/*" className="hidden" onChange={handlePhotoSelect} data-testid="photo-file-input" />
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                <div className="flex items-center gap-3">
                  <User size={18} className="text-slate-400" />
                  <span className="text-sm font-medium text-slate-600">Name</span>
                </div>
                <span className="text-sm font-semibold text-slate-900">{user?.name || 'Not set'}</span>
              </div>
              {isAdmin && (
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                  <div className="flex items-center gap-3">
                    <Home size={18} className="text-slate-400" />
                    <span className="text-sm font-medium text-slate-600">Company</span>
                  </div>
                  <span className="text-sm font-semibold text-slate-900">{user?.companyName || 'Not set'}</span>
                </div>
              )}
              {!isAdmin && user?.companyName && (
                <div className="flex items-center justify-between p-4 bg-blue-50 rounded-xl border border-blue-100">
                  <div className="flex items-center gap-3">
                    <Home size={18} className="text-blue-500" />
                    <span className="text-sm font-medium text-blue-700">Your Admin</span>
                  </div>
                  <span className="text-sm font-semibold text-blue-800">{user?.companyName}</span>
                </div>
              )}
              <div className="flex items-center justify-between gap-3 p-4 bg-slate-50 rounded-xl">
                <div className="flex items-center gap-3 flex-shrink-0">
                  <Mail size={18} className="text-slate-400" />
                  <span className="text-sm font-medium text-slate-600">Email</span>
                </div>
                <span className="text-sm font-semibold text-slate-900 min-w-0 truncate text-right" title={user?.email}>{user?.email}</span>
              </div>
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                <div className="flex items-center gap-3">
                  <Shield size={18} className="text-slate-400" />
                  <span className="text-sm font-medium text-slate-600">Role</span>
                </div>
                <span className="badge-3d badge-success text-sm">{user?.role}</span>
              </div>
            </div>
          </motion.div>

          {/* Subscription Section - Admin Only */}
          {isAdmin && subscription && (
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15 }}
              className="card-3d p-6"
            >
              {/* Past Due Warning */}
              {subscription.subscription_status === 'past_due' && (
                <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-3">
                  <AlertTriangle size={20} className="text-red-500 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-red-700">Payment Failed</p>
                    <p className="text-xs text-red-600">Please update your payment method to continue using all features.</p>
                  </div>
                  <button
                    onClick={handleManageBilling}
                    className="btn-3d h-10 px-4 rounded-xl font-semibold text-sm bg-red-500 text-white hover:bg-red-600"
                  >
                    Fix Payment
                  </button>
                </div>
              )}

              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center">
                    <Crown size={20} className="text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-slate-900">Subscription</h3>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-lg font-bold text-slate-900 capitalize">{subscription.current_plan}</span>
                      {getStatusBadge(subscription.subscription_status)}
                    </div>
                  </div>
                </div>
              </div>

              {/* Trial Info */}
              {subscription.subscription_status === 'trialing' && subscription.trial_end_date && (
                <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-xl">
                  <p className="text-sm text-blue-700">
                    <span className="font-semibold">Trial ends:</span> {formatDate(subscription.trial_end_date)}
                  </p>
                </div>
              )}

              {/* Usage Stats */}
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="p-4 bg-slate-50 rounded-xl">
                  <div className="flex items-center gap-2 mb-2">
                    <Home size={16} className="text-blue-500" />
                    <span className="text-xs font-medium text-slate-500">Properties</span>
                  </div>
                  <div className="flex items-baseline gap-1">
                    <span className="text-2xl font-bold text-slate-900">{subscription.properties_used}</span>
                    <span className="text-sm text-slate-500">/ {subscription.property_limit}</span>
                  </div>
                  {!subscription.can_add_property && (
                    <p className="text-xs text-amber-600 mt-1">Limit reached</p>
                  )}
                </div>
                <div className="p-4 bg-slate-50 rounded-xl">
                  <div className="flex items-center gap-2 mb-2">
                    <Users size={16} className="text-emerald-500" />
                    <span className="text-xs font-medium text-slate-500">Team Members</span>
                  </div>
                  <div className="flex items-baseline gap-1">
                    <span className="text-2xl font-bold text-slate-900">{subscription.users_used}</span>
                    <span className="text-sm text-slate-500">
                      / {subscription.user_limit === -1 ? '∞' : subscription.user_limit}
                    </span>
                  </div>
                  {!subscription.can_add_user && (
                    <p className="text-xs text-amber-600 mt-1">Limit reached</p>
                  )}
                </div>
              </div>

              {/* Next Billing */}
              {subscription.subscription_status === 'active' && subscription.current_period_end && (
                <p className="text-sm text-slate-500 mb-4">
                  Next billing date: {formatDate(subscription.current_period_end)}
                </p>
              )}

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={() => navigate('/pricing')}
                  className="btn-3d btn-3d-primary flex-1 h-12 rounded-xl font-semibold flex items-center justify-center gap-2"
                  data-testid="upgrade-plan-button"
                >
                  <Crown size={18} />
                  {isIOS() ? 'View Plans' : (subscription.current_plan === 'enterprise' ? 'View Plans' : 'Upgrade Plan')}
                </button>
                {!isIOS() && subscription.stripe_customer_id && (
                  <button
                    onClick={handleManageBilling}
                    className="btn-3d btn-3d-outline flex-1 h-12 rounded-xl font-semibold flex items-center justify-center gap-2"
                    data-testid="manage-billing-button"
                  >
                    <CreditCard size={18} />
                    Manage Billing
                  </button>
                )}
                {isIOS() && subscription.subscription_source === 'apple' && (
                  <button
                    onClick={showManageSubscriptions}
                    className="btn-3d btn-3d-outline flex-1 h-12 rounded-xl font-semibold flex items-center justify-center gap-2"
                    data-testid="manage-apple-subscription-button"
                  >
                    <CreditCard size={18} />
                    Manage Subscription
                  </button>
                )}
              </div>
            </motion.div>
          )}

          {/* Notifications (unified: Push + Email) */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="card-3d p-6"
          >
            <button
              onClick={() => setNotifOpen((v) => !v)}
              className="w-full flex items-center gap-3 text-left"
              data-testid="notifications-toggle"
            >
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-emerald-500 flex items-center justify-center flex-shrink-0">
                <Bell size={20} className="text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-xl font-bold text-slate-900">Notifications</h3>
                <p className="text-sm text-slate-500 truncate">Push and email send the same alerts. Tap to manage.</p>
              </div>
              <motion.div animate={{ rotate: notifOpen ? 180 : 0 }} transition={{ duration: 0.2 }}>
                <ChevronDown size={22} className="text-slate-400" />
              </motion.div>
            </button>

            <AnimatePresence initial={false}>
              {notifOpen && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.3 }}
                  className="overflow-hidden"
                >
                  <p className="text-sm text-slate-600 mb-5 mt-4">
                    Get alerted the moment a guest checks out and when cleaning is updated. Push and email send the <strong>same alerts</strong>.
                  </p>

                  <div className="space-y-3">
              {/* Push channel */}
              <div className="flex items-center justify-between gap-4 p-4 bg-slate-50 rounded-2xl border border-slate-200">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center flex-shrink-0">
                    <Smartphone size={20} className="text-white" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-bold text-slate-800">Push notifications</p>
                    <p className="text-xs text-slate-500">Instant pop-up alerts on your phone via the app</p>
                    {!isNative && (
                      <p className="text-[11px] font-semibold text-blue-600 mt-1">Requires the GuestCheckout mobile app</p>
                    )}
                  </div>
                </div>
                <ToggleSwitch
                  checked={pushNotifications}
                  onChange={handleTogglePushNotifications}
                  activeColor="blue"
                  testId="toggle-push-notifications"
                />
              </div>

              {/* Email channel */}
              <div className="flex items-center justify-between gap-4 p-4 bg-slate-50 rounded-2xl border border-slate-200">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center flex-shrink-0">
                    <Mail size={20} className="text-white" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-bold text-slate-800">Email notifications</p>
                    <p className="text-xs text-slate-500">The same alerts sent to your inbox. Works everywhere</p>
                  </div>
                </div>
                <ToggleSwitch
                  checked={emailNotificationsEnabled}
                  onChange={handleToggleEmailNotifications}
                  disabled={loadingEmailSettings}
                  activeColor="purple"
                  testId="toggle-email-notifications"
                />
              </div>

              {/* Staff: checkout alerts are mandatory (choose channel, can't turn both off) */}
              {!isAdmin && (
                <div className="flex items-start gap-2 p-3 bg-blue-50 rounded-xl border border-blue-100" data-testid="staff-checkout-always-on-note">
                  <Shield size={15} className="text-blue-500 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-blue-700">
                    <strong>Checkout alerts are always on.</strong> You can pick email or push (or both), but at least one stays enabled so you never miss a turnover.
                  </p>
                </div>
              )}

              {/* Recipient Selection - Only show when email enabled and for admin */}
              {emailNotificationsEnabled && isAdmin && (
                <div className="p-4 bg-purple-50 rounded-2xl border border-purple-200">
                  <p className="text-sm font-semibold text-purple-800 mb-3">Who should receive checkout emails?</p>
                  <div className="space-y-2">
                    {[
                      { value: 'admin', title: 'Admin only', desc: 'Only admins receive checkout emails' },
                      { value: 'staff', title: 'Assigned staff only', desc: 'Only staff assigned to the property' },
                      { value: 'both', title: 'Admin & assigned staff', desc: 'Both admin and assigned staff receive emails' }
                    ].map((opt) => (
                      <label
                        key={opt.value}
                        className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${
                          emailRecipients === opt.value ? 'bg-white border-purple-400 ring-1 ring-purple-300' : 'bg-white border-purple-100 hover:border-purple-300'
                        }`}
                        data-testid={`email-recipients-${opt.value}`}
                      >
                        <input
                          type="radio"
                          name="emailRecipients"
                          value={opt.value}
                          checked={emailRecipients === opt.value}
                          onChange={(e) => handleChangeRecipients(e.target.value)}
                          className="w-4 h-4 text-purple-600 focus:ring-purple-500"
                        />
                        <div>
                          <span className="text-sm font-semibold text-slate-700">{opt.title}</span>
                          <p className="text-xs text-slate-500">{opt.desc}</p>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* Cleaning event preferences - all users (admin + staff) */}
              <div className="p-4 bg-emerald-50 rounded-2xl border border-emerald-200">
                  <p className="text-sm font-semibold text-emerald-800 mb-1">Cleaning updates</p>
                  <p className="text-xs text-emerald-600 mb-4">
                    Choose which cleaning events alert you (via the channels above).
                  </p>
                  
                  <div className="space-y-3">
                    {/* Notify on Ready */}
                    <div className="flex items-center justify-between gap-4 p-3 bg-white rounded-xl border border-emerald-100">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-9 h-9 rounded-lg bg-emerald-100 flex items-center justify-center flex-shrink-0">
                          <CheckCircle2 size={15} className="text-emerald-600" />
                        </div>
                        <div className="min-w-0">
                          <span className="text-sm font-semibold text-slate-700">Property ready</span>
                          <p className="text-xs text-slate-500">When cleaning is complete and ready for guests</p>
                        </div>
                      </div>
                      <ToggleSwitch
                        checked={notifyOnReady}
                        onChange={handleToggleReadyNotify}
                        activeColor="emerald"
                        testId="toggle-property-ready"
                      />
                    </div>

                    {/* Notify on Late Checkout */}
                    <div className="flex items-center justify-between gap-4 p-3 bg-white rounded-xl border border-emerald-100">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-9 h-9 rounded-lg bg-orange-100 flex items-center justify-center flex-shrink-0">
                          <CalendarClock size={15} className="text-orange-600" />
                        </div>
                        <div className="min-w-0">
                          <span className="text-sm font-semibold text-slate-700">Late checkout</span>
                          <p className="text-xs text-slate-500">When an admin schedules a late checkout</p>
                        </div>
                      </div>
                      <ToggleSwitch
                        checked={notifyOnLateCheckout}
                        onChange={handleToggleLateCheckoutNotify}
                        activeColor="emerald"
                        testId="toggle-late-checkout"
                      />
                    </div>
                  </div>
                </div>
            </div>
          </motion.div>
              )}
            </AnimatePresence>
          </motion.div>

          {/* User Guide (collapsible dropdown — same style as Notifications) */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35 }}
            className="card-3d p-6"
          >
            <button
              onClick={() => setGuideOpen((v) => !v)}
              className="w-full flex items-center gap-3 text-left"
              data-testid="guide-toggle"
            >
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center flex-shrink-0">
                <BookOpen size={20} className="text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-xl font-bold text-slate-900">User Guide</h3>
                <p className="text-sm text-slate-500 truncate">Learn how everything works, step by step. Tap to open.</p>
              </div>
              <motion.div animate={{ rotate: guideOpen ? 180 : 0 }} transition={{ duration: 0.2 }}>
                <ChevronDown size={22} className="text-slate-400" />
              </motion.div>
            </button>

            <AnimatePresence initial={false}>
              {guideOpen && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.3 }}
                  className="overflow-hidden"
                >
                  <div className="pt-5 space-y-3">
                    <button
                      onClick={() => navigate('/guide')}
                      className="btn-3d btn-3d-accent w-full h-14 rounded-2xl font-semibold flex items-center justify-center gap-2"
                      data-testid="view-guide-button"
                    >
                      <BookOpen size={18} />
                      View Full Guide
                    </button>
                    <button
                      onClick={() => navigate('/guide/staff')}
                      className="w-full h-14 rounded-2xl font-semibold flex items-center justify-center gap-2 bg-white border-2 border-slate-200 text-slate-700 hover:border-emerald-300 hover:bg-emerald-50 transition-colors"
                      data-testid="view-staff-guide-button"
                    >
                      <Users size={18} />
                      Staff Guide
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>

          {/* Need Help - AI chat (collapsible dropdown — same style as Notifications) */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.38 }}
            className="card-3d p-6"
          >
            <button
              onClick={() => setHelpOpen((v) => !v)}
              className="w-full flex items-center gap-3 text-left"
              data-testid="help-toggle"
            >
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-emerald-500 flex items-center justify-center flex-shrink-0">
                <MessageSquare size={20} className="text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-xl font-bold text-slate-900">Need Help?</h3>
                <p className="text-sm text-slate-500 truncate">Chat with our assistant. Tap to open.</p>
              </div>
              <motion.div animate={{ rotate: helpOpen ? 180 : 0 }} transition={{ duration: 0.2 }}>
                <ChevronDown size={22} className="text-slate-400" />
              </motion.div>
            </button>

            <AnimatePresence initial={false}>
              {helpOpen && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.3 }}
                  className="overflow-hidden"
                >
                  <div className="pt-5 space-y-3">
                    <p className="text-sm text-slate-600">
                      Have a question or something not working? Chat with our assistant — it answers instantly and can open a support ticket for you.
                    </p>
                    <button
                      onClick={() => setChatOpen(true)}
                      className="btn-3d w-full h-14 rounded-2xl font-semibold flex items-center justify-center gap-2 bg-gradient-to-r from-blue-500 to-emerald-500 text-white"
                      data-testid="open-support-chat-button"
                    >
                      <MessageSquare size={18} />
                      Chat with us
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>

          {/* Owner Dashboard entry (private — owner only) */}
          {isOwner && (
            <motion.button
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.39 }}
              onClick={() => navigate('/owner')}
              data-testid="owner-dashboard-card"
              className="w-full text-left rounded-3xl p-5 flex items-center gap-4 shadow-lg hover:shadow-xl transition-all active:scale-[0.99]"
              style={{ background: 'linear-gradient(135deg, #0F172A 0%, #1E293B 100%)' }}
            >
              <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-blue-500 to-emerald-500 flex items-center justify-center flex-shrink-0">
                <Activity size={22} className="text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h3 className="text-lg font-bold text-white">Owner Dashboard</h3>
                  <span className="text-[10px] font-bold uppercase tracking-wide bg-white/10 text-white/80 px-2 py-0.5 rounded-full">Private</span>
                </div>
                <p className="text-xs text-slate-400 font-medium mt-0.5">Live platform stats — you only</p>
              </div>
              <ChevronRight size={20} className="text-slate-400 flex-shrink-0" />
            </motion.button>
          )}

          {/* Logout */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="card-3d p-6"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-red-50 flex items-center justify-center">
                <LogOut size={20} className="text-red-500" />
              </div>
              <h3 className="text-xl font-bold text-slate-900">Logout</h3>
            </div>
            
            <p className="text-sm text-slate-600 mb-4">
              Sign out of your GuestCheckout account
            </p>
            <button
              onClick={handleLogout}
              className="w-full h-14 rounded-2xl font-semibold flex items-center justify-center gap-2 bg-white border-2 border-red-200 text-red-600 hover:bg-red-50 transition-colors"
              data-testid="logout-button"
            >
              <LogOut size={18} />
              Logout
            </button>
          </motion.div>

          {/* Spacer so the last card clears the fixed mobile bottom nav */}
          <div className="h-28 md:hidden" aria-hidden="true" />
        </div>
      </main>

      {/* Edit Account Modal */}
      <AnimatePresence>
        {showEditModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowEditModal(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="bg-white rounded-3xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6 border-b border-slate-100">
                <div className="flex items-center justify-between">
                  <h2 className="text-2xl font-bold gradient-text pb-1">Edit Account</h2>
                  <button
                    onClick={() => setShowEditModal(false)}
                    className="w-10 h-10 rounded-xl bg-slate-100 hover:bg-slate-200 flex items-center justify-center transition-colors"
                  >
                    <X size={20} className="text-slate-600" />
                  </button>
                </div>
              </div>
              
              <div className="p-6 space-y-6">
                <div className="space-y-4">
                  <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                    <User size={18} className="text-blue-500" />
                    Profile
                  </h3>
                  <div>
                    <Label htmlFor="editName" className="text-sm font-semibold text-slate-700">
                      {isAdmin ? 'Your name' : 'Name (shown on Home)'}
                    </Label>
                    <Input
                      id="editName"
                      type="text"
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      placeholder={isAdmin ? 'e.g. Luca' : 'e.g. your name'}
                      className="input-3d mt-2 h-12"
                      data-testid="edit-name-input"
                    />
                  </div>
                  {isAdmin && (
                    <div>
                      <Label htmlFor="editCompanyName" className="text-sm font-semibold text-slate-700">
                        Company name (shown on Home)
                      </Label>
                      <Input
                        id="editCompanyName"
                        type="text"
                        value={editCompanyName}
                        onChange={(e) => setEditCompanyName(e.target.value)}
                        placeholder="e.g. York Airbnb Management"
                        className="input-3d mt-2 h-12"
                        data-testid="edit-company-name-input"
                      />
                    </div>
                  )}
                  <div>
                    <Label htmlFor="editEmail" className="text-sm font-semibold text-slate-700">Email Address</Label>
                    <Input
                      id="editEmail"
                      type="email"
                      value={editEmail}
                      readOnly
                      disabled
                      className="input-3d mt-2 h-12 bg-slate-100 text-slate-500 cursor-not-allowed"
                      data-testid="edit-email-input"
                    />
                    <p className="text-xs text-slate-400 mt-1.5">Your registration email can't be changed.</p>
                  </div>
                </div>

                <div className="space-y-4 pt-4 border-t border-slate-100">
                  <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                    <Lock size={18} className="text-emerald-500" />
                    Change Password
                  </h3>
                  <p className="text-xs text-slate-500">Leave blank to keep your current password</p>
                  <div>
                    <Label htmlFor="currentPassword" className="text-sm font-semibold text-slate-700">Current Password</Label>
                    <Input
                      id="currentPassword"
                      type="password"
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      placeholder="Enter current password"
                      data-testid="current-password-input"
                      className="input-3d mt-2 h-12"
                    />
                  </div>
                  <div>
                    <Label htmlFor="newPassword" className="text-sm font-semibold text-slate-700">New Password</Label>
                    <Input
                      id="newPassword"
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="Enter new password"
                      minLength={6}
                      data-testid="new-password-input"
                      className="input-3d mt-2 h-12"
                    />
                  </div>
                  <div>
                    <Label htmlFor="confirmPassword" className="text-sm font-semibold text-slate-700">Confirm New Password</Label>
                    <Input
                      id="confirmPassword"
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Confirm new password"
                      minLength={6}
                      data-testid="confirm-password-input"
                      className="input-3d mt-2 h-12"
                    />
                  </div>
                </div>

                <div className="pt-4 border-t border-slate-100">
                  <button
                    onClick={() => { setShowEditModal(false); setDeleteConfirmText(''); setShowDeleteModal(true); }}
                    className="w-full h-12 rounded-2xl border-2 border-red-200 text-red-600 hover:bg-red-50 font-semibold flex items-center justify-center gap-2 text-sm transition-colors"
                    data-testid="open-delete-account-button"
                  >
                    <Trash2 size={16} /> Delete my account
                  </button>
                  <p className="text-xs text-slate-400 mt-2 text-center">
                    {isAdmin ? 'Permanently deletes your company and all its data. This cannot be undone.' : 'Permanently deletes your personal account. This cannot be undone.'}
                  </p>
                </div>
              </div>

              <div className="p-6 border-t border-slate-100 flex gap-3">
                <button
                  onClick={() => setShowEditModal(false)}
                  className="btn-3d btn-3d-outline flex-1 h-14 rounded-2xl font-semibold"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveProfile}
                  disabled={changingPassword}
                  className="btn-3d btn-3d-accent flex-1 h-14 rounded-2xl font-semibold"
                  data-testid="save-profile-button"
                >
                  {changingPassword ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <SupportChat open={chatOpen} onOpenChange={setChatOpen} />

      {/* Delete Account confirmation modal */}
      <AnimatePresence>
        {showDeleteModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
            onClick={() => { if (!deletingAccount) setShowDeleteModal(false); }}
            data-testid="delete-account-modal"
          >
            <motion.div
              initial={{ scale: 0.95, y: 10 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.95, y: 10 }}
              className="bg-white rounded-3xl w-full max-w-md overflow-hidden shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6">
                <div className="w-12 h-12 rounded-2xl bg-red-100 flex items-center justify-center mb-4">
                  <AlertTriangle size={24} className="text-red-600" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">Delete your account?</h3>
                <p className="text-sm text-slate-600 mb-4">
                  {isAdminUser
                    ? 'This permanently deletes your company and every property, checkout, team member and maintenance record. Any active subscription will be cancelled. This action cannot be undone.'
                    : 'This permanently deletes your personal account. This action cannot be undone.'}
                </p>
                {isIOS() && isAdminUser && (
                  <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-4" data-testid="ios-delete-apple-note">
                    Note: deleting your account does not cancel your Apple subscription. Please cancel it in your Apple ID settings (Settings → Apple ID → Subscriptions).
                  </p>
                )}
                <Label className="text-sm font-semibold text-slate-700">Type <span className="text-red-600">DELETE</span> to confirm</Label>
                <Input
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  placeholder="DELETE"
                  className="input-3d mt-2 h-12"
                  data-testid="delete-confirm-input"
                  autoFocus
                />
              </div>
              <div className="p-6 pt-0 flex gap-3">
                <button
                  onClick={() => setShowDeleteModal(false)}
                  disabled={deletingAccount}
                  className="btn-3d btn-3d-outline flex-1 h-14 rounded-2xl font-semibold"
                  data-testid="cancel-delete-button"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteAccount}
                  disabled={deletingAccount || deleteConfirmText.trim().toUpperCase() !== 'DELETE'}
                  className="btn-3d flex-1 h-14 rounded-2xl font-semibold flex items-center justify-center gap-2 disabled:opacity-50"
                  style={{ background: 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)', color: 'white' }}
                  data-testid="confirm-delete-button"
                >
                  {deletingAccount ? <Loader2 size={18} className="animate-spin" /> : <Trash2 size={18} />}
                  {deletingAccount ? 'Deleting...' : 'Delete forever'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showCrop && cropSrc && (
          <ImageCropModal
            src={cropSrc}
            saving={uploadingPhoto}
            onCancel={() => { if (!uploadingPhoto) { setShowCrop(false); setCropSrc(null); } }}
            onSave={uploadCroppedPhoto}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
